// src/pages/BusinessDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from '../components/Global/sidebar'; // Ensure this path matches your file structure

// PrimeReact + Chart.js
import { Chart } from 'primereact/chart';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

// Import components
import TopHeader from '../components/dashboard/TopHeader/TopHeader';
import TestModeBanner from '../components/dashboard/TestModeBanner';
import AnalyticsTabs from '../components/dashboard/Analytics/AnalyticsTabs';
import SummaryCards from '../components/dashboard/Analytics/SummaryCards/SummaryCards';
import LatestActivities from '../components/dashboard/Analytics/LatestActivities/LatestActivities';
import ClaimStatistics from '../components/dashboard/Analytics/ClaimStatistics';

// API Utilities
import { authenticatedFetch, clearStoredCredentials } from '../utils/api';

// Import styles
import '../styles/sidebar.css';
import '../styles/dashboard.css';

// API Base
const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').trim().replace(/\/$/, '');

// Report Endpoints
const REPORT_ENDPOINTS = {
  TOP_5_PRODUCTS: `${API_BASE}/Report/products/top-5`,
  TOTAL_AMOUNT: `${API_BASE}/Report/products/total-amount`,
  STAFF: (productId, companyId) => `${API_BASE}/Report/product/${productId}/company/${companyId}/staff`,
  SUMMARY: (productId, companyId) => `${API_BASE}/Report/product/${productId}/company/${companyId}/summary`,
  PREMIUM: (productId, companyId) => `${API_BASE}/Report/product/${productId}/company/${companyId}/premium`,
  COVERAGE: (productId, companyId) => `${API_BASE}/Report/product/${productId}/company/${companyId}/coverage`,
  DEPARTMENTS: (productId, companyId) => `${API_BASE}/Report/product/${productId}/company/${companyId}/departments`
};

// ✅ ULTRA-SAFE: Format currency - handles ALL types
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₦0';
  if (typeof value === 'object' && value !== null) {
    value = value.value || value.amount || value.total || 0;
  }
  const num = parseFloat(value);
  if (isNaN(num)) return '₦0';
  if (num >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
  return `₦${num.toLocaleString()}`;
};

// ✅ ULTRA-SAFE: Format percentage
const formatPercent = (value, showSign = true) => {
  if (value === null || value === undefined) return '0%';
  if (typeof value === 'object' && value !== null) {
    value = value.value || value.percent || 0;
  }
  const num = parseFloat(value);
  if (isNaN(num)) return '0%';
  const sign = showSign && num > 0 ? '+' : '';
  return `${sign}${num.toFixed(1)}%`;
};

// ✅ ULTRA-SAFE: Format date - handles ALL edge cases
const formatDate = (dateValue) => {
  if (!dateValue) return 'Not set';
  if (typeof dateValue === 'object' && dateValue !== null) {
    dateValue = dateValue.value || dateValue.date || dateValue.toString?.() || '';
    if (dateValue === '[object Object]' || !dateValue) return 'Not set';
  }
  const dateStr = String(dateValue);
  if (!dateStr || dateStr === 'null' || dateStr === 'undefined') return 'Not set';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return 'Invalid date';
  }
};

// ✅ ULTRA-SAFE: Extract string value from ANY type
const safeString = (value, fallback = 'N/A') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value || fallback;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && value !== null) {
    const extracted = value.value || value.name || value.label || value.date || value.toString?.();
    if (extracted && extracted !== '[object Object]') return String(extracted);
    try {
      const jsonStr = JSON.stringify(value);
      if (jsonStr && jsonStr !== '{}' && jsonStr !== 'null') return jsonStr;
    } catch {}
    return fallback;
  }
  const str = String(value);
  return str !== '[object Object]' ? str : fallback;
};

// ✅ ULTRA-SAFE: Extract number value from ANY type
const safeNumber = (value, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number') return isNaN(value) ? fallback : value;
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? fallback : num;
  }
  if (typeof value === 'object' && value !== null) {
    const extracted = value.value || value.count || value.amount || value.total;
    if (extracted !== undefined) return safeNumber(extracted, fallback);
    return fallback;
  }
  return fallback;
};

// ✅ ULTRA-SAFE: Format number for display
const formatNumber = (value) => {
  const num = safeNumber(value, 0);
  return num.toLocaleString();
};

function BusinessDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions');
  const navigate = useNavigate();
  const location = useLocation();

  // Report State
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    return session.companyId || 1;
  });
  const [topProducts, setTopProducts] = useState([]);
  const [productAmountData, setProductAmountData] = useState(null);
  const [reportData, setReportData] = useState({
    summary: null,
    premium: null,
    coverage: null,
    staff: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coverageFilter, setCoverageFilter] = useState(null);

  // Session helpers
  const getSession = () => {
    try {
      const raw = localStorage.getItem('userSession');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return {
        authorizationKey: parsed.authorizationKey || parsed.AuthorizationKey || '',
        token: parsed.token || parsed.Token || '',
        secretKey: parsed.secretKey || parsed.SecretKey || '',
        companyId: parsed.companyId || parsed.CompanyId,
        expiresAt: parsed.expiresAt
      };
    } catch {
      return {};
    }
  };

  const isAuthenticated = () => {
    const session = getSession();
    return !!(session.authorizationKey && session.token && session.secretKey);
  };

  // Fetch Top 5 Products
  const fetchTopProducts = useCallback(async () => {
    try {
      const session = getSession();
      console.log('🔐 Auth Debug - fetchTopProducts:', {
        hasAuthKey: !!session.authorizationKey,
        endpoint: REPORT_ENDPOINTS.TOP_5_PRODUCTS
      });

      const response = await authenticatedFetch(REPORT_ENDPOINTS.TOP_5_PRODUCTS, 'POST', {});
      let products = [];
      
      if (Array.isArray(response)) {
        products = response;
      } else if (response?.success === true || response?.status === 'success') {
        products = response.data || response.result || [];
      } else {
        const msg = (response?.message || '').toLowerCase();
        if (msg.includes('invalid token') || msg.includes('invalid secret') || msg.includes('authorization')) {
          clearStoredCredentials();
          localStorage.removeItem('userSession');
          toast.warning('Session expired. Please log in again.');
          navigate('/login', { replace: true });
          return;
        }
        throw new Error(response.message || 'Failed to fetch products');
      }
      
      const validProducts = Array.isArray(products) ? products : [];
      setTopProducts(validProducts);
      
      if (validProducts.length > 0 && !selectedProduct) {
        setSelectedProduct(validProducts[0].productId || validProducts[0].ProductId);
      }
    } catch (err) {
      console.error('💥 Top products fetch error:', err);
      const errorMsg = err.message?.toLowerCase() || '';
      if (errorMsg.includes('failed to fetch') || err.name === 'TypeError') {
        toast.error('Cannot connect to server');
      } else if (errorMsg.includes('401') || errorMsg.includes('unauthorized')) {
        clearStoredCredentials();
        localStorage.removeItem('userSession');
        toast.warning('Session expired. Please log in again.');
        navigate('/login', { replace: true });
      } else {
        toast.error(err.message || 'Could not load products');
      }
    }
  }, [navigate, selectedProduct]);

  // Fetch Total Amount Per Product
  const fetchProductAmountData = useCallback(async () => {
    try {
      const response = await authenticatedFetch(REPORT_ENDPOINTS.TOTAL_AMOUNT, 'POST', {});
      let labels = [];
      let dataValues = [];
      
      if (response?.success === true || response?.status === 'success') {
        labels = response.labels || [];
        dataValues = response.datasets?.[0]?.data || [];
      } else if (response?.labels && Array.isArray(response.labels)) {
        labels = response.labels;
        dataValues = response.datasets?.[0]?.data || [];
      }
      
      if (labels.length > 0 && dataValues.length > 0) {
        setProductAmountData({
          labels,
          datasets: [{
            label: 'Total Premium',
            data: dataValues,
            backgroundColor: ['#F89423', '#8b5cf6', '#10b981', '#f59e0b', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#22c55e', '#eab308', '#a855f7', '#06b6d4', '#84cc16'],
            borderColor: '#ea7c1f',
            borderWidth: 1,
            borderRadius: 6
          }]
        });
      }
    } catch (err) {
      console.error('💥 Product amount fetch error:', err);
    }
  }, []);

  // Fetch Report Data for selected product/company
  const fetchReportData = useCallback(async (productId, companyId) => {
    if (!productId || !companyId) return;
    try {
      setLoading(true);
      setError(null);
      
      const [summaryRes, premiumRes, coverageRes, staffRes] = await Promise.all([
        authenticatedFetch(REPORT_ENDPOINTS.SUMMARY(productId, companyId), 'POST', {}),
        authenticatedFetch(REPORT_ENDPOINTS.PREMIUM(productId, companyId), 'POST', {}),
        authenticatedFetch(REPORT_ENDPOINTS.COVERAGE(productId, companyId), 'POST', {}),
        authenticatedFetch(REPORT_ENDPOINTS.STAFF(productId, companyId), 'POST', {})
      ]);

      console.log('🔍 RAW PREMIUM RESPONSE:', premiumRes);

      const validate = (res, name) => {
        if (Array.isArray(res)) return res;
        if (res?.success === true || res?.status === 'success') {
          return res.data || res.result || res;
        }
        if (res && (res.startDate || res.endDate || res.totalCollected !== undefined)) {
          return res;
        }
        const msg = (res?.message || '').toLowerCase();
        if (msg.includes('expired') || msg.includes('unauthorized') || msg.includes('invalid token')) {
          clearStoredCredentials();
          localStorage.removeItem('userSession');
          toast.warning(res.message || 'Session expired');
          navigate('/login', { replace: true });
          return null;
        }
        console.warn(`⚠️ ${name} failed:`, res);
        return null;
      };

      const validSummary = validate(summaryRes, 'summary');
      const validPremium = validate(premiumRes, 'premium');
      const validCoverage = validate(coverageRes, 'coverage');
      const validStaff = validate(staffRes, 'staff') || [];

      console.log('📊 Report Data Loaded:', {
        summary: validSummary,
        premium: validPremium,
        coverage: validCoverage,
        staffCount: validStaff?.length
      });

      setReportData({
        summary: validSummary,
        premium: validPremium,
        coverage: validCoverage,
        staff: Array.isArray(validStaff) ? validStaff : []
      });
    } catch (err) {
      console.error('💥 Report data fetch error:', err);
      const errorMsg = err.message?.toLowerCase() || '';
      if (errorMsg.includes('failed to fetch')) {
        setError('Cannot connect to server');
        toast.error('Cannot connect to server');
      } else if (errorMsg.includes('401') || errorMsg.includes('unauthorized')) {
        clearStoredCredentials();
        localStorage.removeItem('userSession');
        toast.warning('Session expired');
        navigate('/login', { replace: true });
      } else {
        setError(err.message || 'Could not load report data');
        toast.error(err.message || 'Could not load report data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Initialize on mount
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    const session = getSession();
    console.log('🔐 Dashboard mounted - Session:', {
      authenticated: isAuthenticated(),
      hasAuthKey: !!session.authorizationKey,
      hasToken: !!session.token,
      hasSecret: !!session.secretKey
    });
    fetchTopProducts();
    fetchProductAmountData();
  }, [fetchTopProducts, fetchProductAmountData]);

  useEffect(() => {
    if (topProducts.length > 0) {
      console.log('✅ Top products loaded:', topProducts.length, 'items');
    }
  }, [topProducts]);

  useEffect(() => {
    if (productAmountData?.labels?.length > 0) {
      console.log('✅ Product amount chart data loaded:', productAmountData.labels.length, 'labels');
    }
  }, [productAmountData]);

  useEffect(() => {
    if (selectedProduct && selectedCompany) {
      fetchReportData(selectedProduct, selectedCompany);
    }
  }, [selectedProduct, selectedCompany, fetchReportData]);

  // Handlers
  const handleNavClick = (path, e) => {
    e?.preventDefault();
    if (path) navigate(path);
    if (window.innerWidth < 992) setSidebarOpen(false);
  };

  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  const handleProductChange = (e) => {
    const productId = Number(e.target.value);
    setSelectedProduct(productId);
    setCoverageFilter(null);
  };

  const handleCompanyChange = (e) => {
    const companyId = Number(e.target.value);
    if (companyId > 0) {
      setSelectedCompany(companyId);
      setCoverageFilter(null);
    }
  };

  const handleRefresh = () => {
    if (selectedProduct && selectedCompany) {
      setLoading(true);
      fetchReportData(selectedProduct, selectedCompany);
      fetchProductAmountData();
      toast.success('Data refreshed');
    }
  };

  const handleCoverageSelect = (status) => {
    setCoverageFilter(status === 'active' ? 'Paid' : status);
  };

  // ✅ MEMOIZED: Filter staff by status
  const filteredStaff = useMemo(() => {
    if (!coverageFilter) return reportData.staff || [];
    return (reportData.staff || []).filter(s => 
      (s.PaymentStatus || s.status || '').toLowerCase().includes(coverageFilter.toLowerCase())
    );
  }, [reportData.staff, coverageFilter]);

  // ✅ DYNAMIC: Calculate stats
  const summary = reportData.summary || {};
  const premium = reportData.premium || {};

  // ✅ ULTRA-SAFE: Extract subscription data
  const subscriptionData = useMemo(() => {
    const data = premium?.data || premium;
    let startDate = null;
    let endDate = null;
    
    if (data?.startDate) {
      if (typeof data.startDate === 'string') {
        startDate = data.startDate;
      } else if (typeof data.startDate === 'object' && data.startDate !== null) {
        startDate = data.startDate.value || data.startDate.date || data.startDate.toString?.() || null;
        if (startDate === '[object Object]') startDate = null;
      }
    }
    
    if (data?.endDate) {
      if (typeof data.endDate === 'string') {
        endDate = data.endDate;
      } else if (typeof data.endDate === 'object' && data.endDate !== null) {
        endDate = data.endDate.value || data.endDate.date || data.endDate.toString?.() || null;
        if (endDate === '[object Object]') endDate = null;
      }
    }
    
    const isExpired = data?.isExpired === true || data?.isExpired === 'true' || data?.isExpired === 1;
    const daysRemaining = safeNumber(data?.daysRemaining, 0);
    
    const result = {
      startDate: startDate,
      endDate: endDate,
      isExpired: isExpired,
      daysRemaining: daysRemaining,
      status: safeString(data?.subscriptionStatus || data?.status, isExpired ? 'Expired' : 'Active')
    };
    
    console.log('📅 SUBSCRIPTION DATA EXTRACTED:', {
      rawPremium: premium,
      extractedData: data,
      result
    });
    
    return result;
  }, [premium]);

  const staffList = filteredStaff.length > 0 ? filteredStaff : (reportData.staff || []);

  // Calculate from filtered/local data
  const calculatedTotalStaff = staffList.length;
  const calculatedActiveStaff = staffList.filter(s => 
    (s.PaymentStatus || s.status || '').toLowerCase() === 'paid'
  ).length;
  const calculatedTotalPremium = staffList.reduce((sum, s) => 
    sum + parseFloat(s.TotalPremium || s.premium || s.TotalAmount || 0), 0
  );
  const calculatedPaidCount = staffList.filter(s => 
    (s.PaymentStatus || s.status || '').toLowerCase() === 'paid'
  ).length;
  const calculatedStaffCount = staffList.length;
  const calculatedAvgPremium = calculatedStaffCount > 0 ? calculatedTotalPremium / calculatedStaffCount : 0;

  // ✅ ULTRA-SAFE: Get display values - ENSURE ALL ARE NUMBERS
  const hasApiPremiumData = premium && 
    premium.totalCollected !== undefined && 
    premium.totalCollected !== null;

  const displayTotalCollected = safeNumber(hasApiPremiumData ? premium.totalCollected : calculatedTotalPremium, 0);
  const displayAvgPremium = safeNumber(premium?.avgPremium !== undefined ? premium.avgPremium : calculatedAvgPremium, 0);
  const displayTotalStaff = safeNumber(summary?.totalStaff !== undefined ? summary.totalStaff : calculatedTotalStaff, 0);
  const displayActiveStaff = safeNumber(summary?.activeStaff !== undefined ? summary.activeStaff : calculatedActiveStaff, 0);
  const displayPaidCount = safeNumber(premium?.paidCount !== undefined ? premium.paidCount : calculatedPaidCount, 0);
  const displayStaffCount = safeNumber(premium?.staffCount !== undefined ? premium.staffCount : calculatedStaffCount, 0);
  const expiredStaffCount = safeNumber(summary?.expiredStaff, 0);

  const activeRate = displayTotalStaff > 0 ? (displayActiveStaff / displayTotalStaff) * 100 : 0;
  const paymentRate = displayStaffCount > 0 ? (displayPaidCount / displayStaffCount) * 100 : 0;

  // ✅ Subscription status styling helper
  const getSubscriptionStyle = () => {
    if (subscriptionData.isExpired) {
      return { bg: '#fee2e2', color: '#991b1b', icon: 'fa-times-circle', borderColor: '#dc2626', label: 'Expired' };
    }
    if (subscriptionData.daysRemaining <= 30 && subscriptionData.daysRemaining > 0) {
      return { bg: '#fef3c7', color: '#92400e', icon: 'fa-exclamation-triangle', borderColor: '#f59e0b', label: 'Expiring Soon' };
    }
    return { bg: '#d1fae5', color: '#065f46', icon: 'fa-check-circle', borderColor: '#10b981', label: 'Active' };
  };

  const subscriptionStyle = getSubscriptionStyle();
  const hasSubscriptionData = subscriptionData.startDate || subscriptionData.endDate;

  // Chart Components
  const ProductAmountChart = ({ data }) => {
    const chartData = data || {
      labels: ['Loading...'],
      datasets: [{ label: 'Premium', data: [0], backgroundColor: ['#F89423'] }]
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { callback: (v) => '₦' + (v/1000000).toFixed(1) + 'M', color: '#6b7280' } },
        x: { grid: { display: false }, ticks: { color: '#6b7280', maxRotation: 45 } }
      }
    };
    return <Chart type="bar" data={chartData} options={options} className="w-100 h-100" />;
  };

  const CoverageStatusChart = ({ data, onStatusSelect }) => {
    const chartData = data || {
      labels: ['Active', 'Expired', 'Inactive'],
      datasets: [{ data: [0, 0, 0], backgroundColor: ['#10b981', '#ef4444', '#6b7280'], borderWidth: 3, borderColor: '#fff' }]
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 15 } } },
      onClick: (evt, activeElements) => {
        if (activeElements?.[0] && onStatusSelect) {
          const idx = activeElements[0].index;
          onStatusSelect(['active', 'expired', 'inactive'][idx]);
        }
      }
    };
    return <Chart type="doughnut" data={chartData} options={options} className="w-100 h-100" />;
  };

  const coverageChartData = reportData.coverage ? {
    labels: ['Active', 'Expired', 'Inactive'],
    datasets: [{
      data: [
        safeNumber(reportData.coverage.active, 0), 
        safeNumber(reportData.coverage.expired, 0), 
        safeNumber(reportData.coverage.inactive, 0)
      ],
      backgroundColor: ['#10b981', '#ef4444', '#6b7280'],
      borderWidth: 3,
      borderColor: '#fff'
    }]
  } : null;

  return (
    <div className="business-dashboard-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* ✅ Left Sidebar (Using Reusable Component) */}
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        onNavClick={handleNavClick} 
        onLogout={handleLogout} 
      />

      {/* Main Content */}
      <div className="main-content">
        <TopHeader 
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          pageTitle="Analytics Dashboard"
          pageIcon="fa-chart-bar"
        />

        <div className="content-wrapper">
          {/* Report Filters */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="toolbar">
                <div className="d-flex gap-3 align-items-end flex-wrap">
                  <div>
                    <label className="form-label small text-muted fw-semibold mb-1">
                      <i className="fas fa-box me-1" /> Select Product
                    </label>
                    <select 
                      className="form-select"
                      style={{ minWidth: '220px' }}
                      value={selectedProduct || ''}
                      onChange={handleProductChange}
                      disabled={loading || topProducts.length === 0}
                    >
                      <option value="" disabled>Choose a product...</option>
                      {topProducts.map(p => (
                        <option key={p.productId || p.ProductId} value={safeNumber(p.productId || p.ProductId, 0)}>
                          {safeString(p.productName || p.ProductName)} • {formatCurrency(p.totalAmount || p.TotalAmount)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {coverageFilter && (
                    <div>
                      <label className="form-label small text-muted fw-semibold mb-1">Filter</label>
                      <Tag 
                        value={coverageFilter} 
                        severity="info" 
                        icon="pi pi-filter"
                        onRemove={() => setCoverageFilter(null)}
                        className="cursor-pointer"
                      />
                    </div>
                  )}
                </div>
                <Button 
                  className="btn btn-warning"
                  onClick={handleRefresh}
                  disabled={loading || !selectedProduct || !selectedCompany}
                  icon={<i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} />}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </div>
          </div>

          {/* ✅ SUBSCRIPTION PERIOD CARD */}
          <div className="card border-0 shadow-sm mb-4 subscription-period-card" data-aos="fade-up">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fw-semibold" style={{ color: '#F89423' }}>
                  <i className="fas fa-calendar-alt me-2"></i>
                  Subscription Period
                </h5>
                <Tag 
                  value={subscriptionStyle.label}
                  icon={`fas ${subscriptionStyle.icon}`}
                  style={{ 
                    backgroundColor: subscriptionStyle.bg, 
                    color: subscriptionStyle.color,
                    border: `1px solid ${subscriptionStyle.borderColor}`,
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '6px 12px'
                  }}
                />
              </div>
              
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="date-info-box">
                    <div className="date-label">
                      <i className="fas fa-play-circle text-success me-2"></i>
                      Start Date
                    </div>
                    <div className="date-value" style={{ fontSize: '14px' }}>
                      {formatDate(subscriptionData.startDate)}
                    </div>
                    {subscriptionData.startDate && (
                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                        {(() => {
                          try {
                            const d = new Date(subscriptionData.startDate);
                            return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
                          } catch { return ''; }
                        })()}
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="date-info-box">
                    <div className="date-label">
                      <i className="fas fa-stop-circle text-danger me-2"></i>
                      End Date
                    </div>
                    <div className="date-value" style={{ fontSize: '14px' }}>
                      {formatDate(subscriptionData.endDate)}
                    </div>
                    {subscriptionData.endDate && (
                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                        {(() => {
                          try {
                            const d = new Date(subscriptionData.endDate);
                            return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
                          } catch { return ''; }
                        })()}
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="date-info-box">
                    <div className="date-label">
                      <i className="fas fa-clock text-primary me-2"></i>
                      Time Remaining
                    </div>
                    <div className="date-value" style={{ fontSize: '14px' }}>
                      {subscriptionData.isExpired ? (
                        <span style={{ color: '#dc2626', fontWeight: '700' }}>
                          <i className="fas fa-times-circle me-1"></i>
                          Expired
                        </span>
                      ) : hasSubscriptionData ? (
                        <span style={{ 
                          color: subscriptionData.daysRemaining <= 30 ? '#f59e0b' : '#10b981',
                          fontWeight: '700'
                        }}>
                          <i className="fas fa-hourglass-half me-1"></i>
                          {subscriptionData.daysRemaining} day{subscriptionData.daysRemaining !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span style={{ color: '#6b7280' }}>N/A</span>
                      )}
                    </div>
                    <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                      Status: {subscriptionData.status}
                    </small>
                  </div>
                </div>
              </div>

              {/* Alert Messages */}
              {!hasSubscriptionData ? (
                <div className="alert alert-info mb-0 mt-3 d-flex align-items-center" style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd', color: '#1e40af' }}>
                  <i className="fas fa-info-circle me-2 fs-5"></i>
                  <div>
                    <strong>No subscription dates set.</strong>
                    <div className="small mb-0">Contact support to set up your subscription period.</div>
                  </div>
                </div>
              ) : subscriptionData.isExpired ? (
                <div className="alert alert-danger mb-0 mt-3 d-flex align-items-center" style={{ backgroundColor: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b' }}>
                  <i className="fas fa-exclamation-triangle me-2 fs-5"></i>
                  <div>
                    <strong>Subscription has expired.</strong>
                    <div className="small mb-0">Please renew to continue accessing services.</div>
                  </div>
                </div>
              ) : subscriptionData.daysRemaining <= 30 && subscriptionData.daysRemaining > 0 ? (
                <div className="alert alert-warning mb-0 mt-3 d-flex align-items-center" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
                  <i className="fas fa-exclamation-circle me-2 fs-5"></i>
                  <div>
                    <strong>Subscription expiring soon!</strong>
                    <div className="small mb-0">{subscriptionData.daysRemaining} day{subscriptionData.daysRemaining !== 1 ? 's' : ''} remaining. Consider renewing.</div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-success mb-0 mt-3 d-flex align-items-center" style={{ backgroundColor: '#d1fae5', border: '1px solid #a7f3d0', color: '#065f46' }}>
                  <i className="fas fa-check-circle me-2 fs-5"></i>
                  <div>
                    <strong>Subscription is active.</strong>
                    <div className="small mb-0">{subscriptionData.daysRemaining} days remaining until renewal.</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <AnalyticsTabs activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Summary Cards - 3 COLUMNS */}
          <div className="row mb-4">
            {/* Total Staff Card */}
            <div className="col-lg-4 col-md-6 mb-3">
              <div className="summary-card" style={{ backgroundColor: '#fef3e2', borderLeft: '4px solid #F89423' }} data-aos="fade-up">
                <div className="card-body p-4">
                  <p className="text-muted small mb-1">Total Staff</p>
                  <h3 className="mb-1" style={{ color: '#F89423', fontWeight: '700' }}>
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-2" style={{width:'1rem',height:'1rem'}}></span>
                    ) : (
                      formatNumber(displayTotalStaff)
                    )}
                  </h3>
                  <small className="text-muted">{formatNumber(displayActiveStaff)} active</small>
                  <div className="mt-2">
                    <ProgressBar value={activeRate} style={{ height: '5px' }} showValue={false} />
                    <small className="text-muted ms-1">{formatPercent(activeRate, false)} active</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Collected Card */}
            <div className="col-lg-4 col-md-6 mb-3">
              <div className="summary-card" style={{ backgroundColor: '#f0fdf4', borderLeft: '4px solid #10b981' }} data-aos="fade-up" data-aos-delay="100">
                <div className="card-body p-4">
                  <p className="text-muted small mb-1">Total Collected</p>
                  <h3 className="mb-1" style={{ color: '#10b981', fontWeight: '700' }}>
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-2" style={{width:'1rem',height:'1rem'}}></span>
                    ) : (
                      formatCurrency(displayTotalCollected)
                    )}
                  </h3>
                  <small className="text-muted">{formatCurrency(displayAvgPremium)}/staff avg</small>
                  <div className="mt-2">
                    <ProgressBar value={paymentRate} style={{ height: '5px' }} showValue={false} />
                    <small className="text-muted ms-1">{formatNumber(displayPaidCount)}/{formatNumber(displayStaffCount)} paid</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Coverage Card */}
            <div className="col-lg-4 col-md-6 mb-3">
              <div className="summary-card" style={{ backgroundColor: '#eff6ff', borderLeft: '4px solid #3b82f6' }} data-aos="fade-up" data-aos-delay="200">
                <div className="card-body p-4">
                  <p className="text-muted small mb-1">Active Coverage</p>
                  <h3 className="mb-1" style={{ color: '#3b82f6', fontWeight: '700' }}>
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-2" style={{width:'1rem',height:'1rem'}}></span>
                    ) : (
                      formatNumber(displayActiveStaff)
                    )}
                  </h3>
                  <small className="text-muted">{formatNumber(expiredStaffCount)} expired</small>
                  <Tag 
                    value={expiredStaffCount > 0 ? `⚠️ ${expiredStaffCount} need renewal` : 'All current'} 
                    severity={expiredStaffCount > 0 ? 'warning' : 'success'}
                    className="mt-2"
                    style={{ fontSize: '11px' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="row mb-4">
            {/* Product Amount Bar Chart */}
            <div className="col-lg-8 mb-4" data-aos="fade-up">
              <Card className="border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-semibold">💰 Premium by Product</h5>
                  <Button 
                    icon="pi pi-refresh" 
                    className="p-button-text p-button-sm"
                    onClick={fetchProductAmountData}
                    disabled={loading}
                  />
                </div>
                <div className="card-body p-0 px-4 pb-4" style={{ height: '320px' }}>
                  {loading ? (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <div className="spinner-border text-warning" role="status" />
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger m-3">{error}</div>
                  ) : (
                    <ProductAmountChart 
                      key={productAmountData ? productAmountData.labels?.join(',') : 'loading'}
                      data={productAmountData} 
                    />
                  )}
                </div>
              </Card>
            </div>

            {/* Coverage Status Doughnut */}
            <div className="col-lg-4 mb-4" data-aos="fade-up" data-aos-delay="100">
              <Card className="border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <h5 className="mb-0 fw-semibold">📊 Coverage Status</h5>
                </div>
                <div className="card-body p-0 px-4 pb-4" style={{ height: '300px' }}>
                  {loading ? (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <div className="spinner-border text-warning" role="status" />
                    </div>
                  ) : (
                    <CoverageStatusChart 
                      data={coverageChartData}
                      onStatusSelect={handleCoverageSelect}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>

          {/* Staff Table */}
          <div className="card border-0 shadow-sm mb-4" data-aos="fade-up">
            <div className="card-body p-4">
              <div className="section-header">
                <div className="section-title">
                  <h5 className="mb-1">👥 Staff Enrollment</h5>
                  <p className="mb-0">
                    {loading ? 'Loading...' : `${filteredStaff.length} records`}
                    {coverageFilter && <span className="ms-2 text-warning">• Filtered</span>}
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <div className="search-box">
                    <i className="fas fa-search"></i>
                    <InputText placeholder="Search staff..." className="w-auto" disabled={loading} />
                  </div>
                  <Button label="Export" icon="pi pi-download" className="p-button-outlined p-button-sm" disabled={loading} />
                </div>
              </div>

              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                    <p className="mt-3 text-muted">Loading staff data...</p>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-circle me-2"></i>{error}
                    <button className="btn btn-sm btn-outline-danger ms-3" onClick={handleRefresh}>Retry</button>
                  </div>
                ) : filteredStaff.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-users fa-4x mb-3 opacity-25"></i>
                    <h5>No staff records found</h5>
                    <p className="mb-0">{reportData.staff?.length === 0 ? 'No staff enrolled yet.' : 'Try clearing the filter.'}</p>
                  </div>
                ) : (
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Premium</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaff.map((staff, index) => (
                        <tr key={staff.id || staff.StaffId || index}>
                          <td>#{safeString(staff.id || staff.StaffId)}</td>
                          <td>
                            <div className="fw-semibold">
                              {safeString(staff.name || staff.FullName || (staff.firstName && staff.lastName ? `${staff.firstName} ${staff.lastName}` : '—'))}
                            </div>
                          </td>
                          <td>
                            <a href={`mailto:${safeString(staff.Email || staff.email, '')}`} className="text-decoration-none">
                              {safeString(staff.Email || staff.email)}
                            </a>
                          </td>
                          <td>{safeString(staff.Phone || staff.phone)}</td>
                          <td>
                            <Tag 
                              value={safeString(staff.PaymentStatus || staff.status || 'Unknown')} 
                              severity={(staff.PaymentStatus || staff.status) === 'Paid' ? 'success' : (staff.PaymentStatus || staff.status) === 'Pending' ? 'warning' : 'secondary'}
                              style={{ fontSize: '11px' }}
                            />
                          </td>
                          <td className="fw-bold text-orange" style={{ color: '#F89423' }}>
                            {formatCurrency(staff.TotalPremium || staff.premium || staff.TotalAmount)}
                           </td>
                          <td>
                            <Button icon="pi pi-eye" className="p-button-text p-button-sm p-button-rounded" onClick={() => toast.info(`View: ${safeString(staff.name || staff.FullName)}`)} />
                           </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted small">
                  Showing <strong>1-{Math.min(10, filteredStaff.length)}</strong> of <strong>{filteredStaff.length}</strong> Staff
                  {coverageFilter && <span className="ms-2 text-warning">• Filtered</span>}
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className="page-item disabled"><a className="page-link" href="/"><i className="fas fa-angle-left"></i></a></li>
                    <li className="page-item active"><a className="page-link" href="/">1</a></li>
                    <li className="page-item"><a className="page-link" href="/">2</a></li>
                    <li className="page-item"><a className="page-link" href="/">3</a></li>
                    <li className="page-item"><a className="page-link" href="/"><i className="fas fa-angle-right"></i></a></li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="row">
            <div className="col-lg-4">
              <LatestActivities />
              <ClaimStatistics />
            </div>
            <div className="col-lg-8">
              {/* Top Products Table */}
              <Card className="border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <h5 className="mb-0 fw-semibold">🏆 Top Performing Products</h5>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '60px' }}>Rank</th>
                          <th>Product</th>
                          <th className="text-end">Total Premium</th>
                          <th className="text-center" style={{ width: '80px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProducts.length === 0 ? (
                          <tr><td colSpan="4" className="text-center py-4 text-muted">Loading...</td></tr>
                        ) : (
                          topProducts.map((product, index) => (
                            <tr 
                              key={product.productId || product.ProductId} 
                              className={selectedProduct === (product.productId || product.ProductId) ? 'table-warning' : ''}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSelectedProduct(safeNumber(product.productId || product.ProductId, 0))}
                            >
                              <td>
                                <span className="fw-bold fs-5" style={{ color: index === 0 ? '#F89423' : index === 1 ? '#6b7280' : index === 2 ? '#9a6b3f' : '#adb5bd' }}>
                                  {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                                </span>
                              </td>
                              <td>
                                <div className="fw-semibold">{safeString(product.productName || product.ProductName)}</div>
                                <small className="text-muted">ID: {safeString(product.productId || product.ProductId)}</small>
                              </td>
                              <td className="text-end fw-bold text-orange" style={{ color: '#F89423' }}>
                                {formatCurrency(product.totalAmount || product.TotalAmount)}
                              </td>
                              <td className="text-center">
                                <Button icon="pi pi-chart-bar" className="p-button-text p-button-sm p-button-rounded" onClick={(e) => { e.stopPropagation(); setSelectedProduct(safeNumber(product.productId || product.ProductId, 0)); }} />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Inline CSS (Sidebar styles removed since they are in Sidebar.css now) */}
      <style>{`
        .business-dashboard-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .summary-card { border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); transition: transform 0.2s; }
        .summary-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important; }
        .toolbar { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
        .search-box { position: relative; }
        .search-box i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
        .search-box .p-inputtext { padding-left: 36px !important; }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
        .section-title h5 { margin: 0; font-weight: 600; }
        .section-title p { margin: 0; color: #6b7280; font-size: 14px; }
        .table-hover tbody tr:hover { background-color: rgba(248, 148, 35, 0.05); }
        .table-hover tbody tr.table-warning { background-color: rgba(248, 148, 35, 0.15) !important; border-left: 3px solid #F89423; }
        .p-tag { font-weight: 500; font-size: 11px; padding: 4px 10px; }
        .p-card { border-radius: 12px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important; }
        .p-progressbar { border-radius: 4px !important; overflow: hidden; }
        
        /* Subscription Period Card Styles */
        .subscription-period-card {
          border-left: 4px solid #F89423 !important;
          background: linear-gradient(135deg, #fff 0%, #fef3e2 100%);
        }
        .date-info-box {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          height: 100%;
          transition: all 0.3s ease;
        }
        .date-info-box:hover {
          border-color: #F89423;
          box-shadow: 0 2px 8px rgba(248, 148, 35, 0.15);
          transform: translateY(-2px);
        }
        .date-label {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6b7280;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
        }
        .date-value {
          font-size: 16px;
          font-weight: 700;
          color: #1f2937;
        }
        
        @media (max-width: 992px) {
          .main-content { margin-left: 0; }
          .toolbar { flex-direction: column; align-items: stretch; }
          .section-header { flex-direction: column; align-items: flex-start; }
        }
        @media (max-width: 768px) {
          .content-wrapper { padding: 15px; }
        }
      `}</style>
    </div>
  );
}

export default BusinessDashboard;